import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LayersIcon from '@mui/icons-material/Layers';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { b2bThriftPlan } from '../../../api/b2bThriftPlan';
import { AsyncDialog, ConfirmationDialog } from '../../../components/AsyncDialog';
import { AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialogData } from '../../../components/AsyncDialog/ConfirmationDialog';
import { AnalyticsCronJob } from '../../../types/analyticsCron';

type CronJobCard = {
  jobName: AnalyticsCronJob;
  label: string;
  description: string;
  /**
   * Position in the nightly run order, shown as a chip.
   *
   * Deliberately NOT the scheduled time: every cron's expression is overridable per
   * environment (and is currently per-minute in local .env), so a hardcoded clock time
   * here would be wrong wherever it matters most. The ORDER is a property of the jobs.
   */
  order: string;
};

type CronGroup = {
  title: string;
  caption: string;
  icon: React.ReactNode;
  jobs: CronJobCard[];
};

/**
 * Grouped by dependency, not alphabetically.
 *
 * The index-fund pair is ordered and separated on purpose: Layer 2 SUMs the rows Layer 1
 * writes, so running it first aggregates nothing. Splitting it into its own panel makes
 * that ordering visible instead of leaving it as a footnote nobody reads.
 */
const CRON_GROUPS: CronGroup[] = [
  {
    title: 'Index Fund Analytics',
    caption: 'Run in order — Layer 2 aggregates what Layer 1 writes.',
    icon: <LayersIcon color="primary" />,
    jobs: [
      {
        jobName: AnalyticsCronJob.SubscribedPortfolioIndexFundDaily,
        label: 'Layer 1 — Per Employee',
        description: 'Walks every employee subscription and writes one row per fund held.',
        order: '1st',
      },
      {
        jobName: AnalyticsCronJob.SubscribedIndexFundAggregationDaily,
        label: 'Layer 2 — Aggregation',
        description: "Sums today's Layer 1 rows per fund. Aggregates nothing if Layer 1 has not run.",
        order: 'last',
      },
    ],
  },
  {
    title: 'Company Analytics',
    caption: 'Independent of each other — run in any order.',
    icon: <QueryStatsIcon color="primary" />,
    jobs: [
      {
        jobName: AnalyticsCronJob.CompanyAnalysisDaily,
        label: 'Company Analysis',
        description: 'Company-level totals, returns and market value.',
        order: '2nd',
      },
      {
        jobName: AnalyticsCronJob.PortfolioAnalysisDaily,
        label: 'Portfolio Analysis',
        description: 'Per-product breakdown and portfolio weighting.',
        order: '3rd',
      },
      {
        jobName: AnalyticsCronJob.CompanyGroupAnalysisDaily,
        label: 'Company Group Analysis',
        description: 'Same figures, split by employee group.',
        order: '4th',
      },
      {
        jobName: AnalyticsCronJob.CompanyDepartmentAnalysisDaily,
        label: 'Company Department Analysis',
        description: 'Same figures, split by department.',
        order: '5th',
      },
      {
        jobName: AnalyticsCronJob.CompanyVestedUnvestedAnalysisDaily,
        label: 'Vested / Unvested Analysis',
        description: 'Vested and unvested split per company.',
        order: '6th',
      },
    ],
  },
];

/** What happened the last time a job was started, in this browser session only. */
type JobOutcome = { status: 'started' | 'failed'; at: Date };

export function AnalyticsCron() {
  const { t } = useTranslation('apiError');
  const { enqueueSnackbar } = useSnackbar();
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);

  /**
   * Per job, not one shared flag: a single flag makes every button spin when any one is
   * pressed, which misreports what is actually running.
   */
  const [runningJobs, setRunningJobs] = useState<Partial<Record<AnalyticsCronJob, boolean>>>({});
  const [outcomes, setOutcomes] = useState<Partial<Record<AnalyticsCronJob, JobOutcome>>>({});

  const anyRunning = Object.values(runningJobs).some(Boolean);

  const onTrigger = async (job: CronJobCard) => {
    const isConfirmed = await asyncDialogRef.current?.show({
      title: `Run ${job.label} now?`,
      description: `This runs immediately for ALL companies, exactly as the nightly schedule would. ${job.description}`,
    });

    if (!isConfirmed) {
      return;
    }

    setRunningJobs((prev) => ({ ...prev, [job.jobName]: true }));

    try {
      await b2bThriftPlan.triggerAnalyticsCron({ jobName: job.jobName });

      // "started", never "completed" — the backend answers as soon as the job begins and
      // reports nothing about how it ends.
      setOutcomes((prev) => ({ ...prev, [job.jobName]: { status: 'started', at: new Date() } }));
      enqueueSnackbar(`${job.label} started — check the service logs for the result.`, { variant: 'success' });
    } catch (err: any) {
      setOutcomes((prev) => ({ ...prev, [job.jobName]: { status: 'failed', at: new Date() } }));
      enqueueSnackbar(`Failed to start ${job.label}: ${t(err?.message)}`, { variant: 'error' });
    } finally {
      setRunningJobs((prev) => ({ ...prev, [job.jobName]: false }));
    }
  };

  const renderOutcome = (jobName: AnalyticsCronJob) => {
    const outcome = outcomes[jobName];

    if (!outcome) {
      return null;
    }

    const time = outcome.at.toLocaleTimeString();

    return outcome.status === 'started' ? (
      <Chip
        size="small"
        color="success"
        variant="outlined"
        icon={<CheckCircleOutlineIcon />}
        label={`Started ${time}`}
      />
    ) : (
      <Chip size="small" color="error" variant="outlined" icon={<ErrorOutlineIcon />} label={`Failed ${time}`} />
    );
  };

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 4 }}>
        <Typography variant="h4">Analytics Cron</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ pt: 2, maxWidth: 720 }}>
          Run an analytics cron now instead of waiting for its nightly schedule.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 6 }}>
        <AlertTitle>These run for every company</AlertTitle>
        Starting a job returns immediately — it does not wait for the job to finish, and the result appears in the
        thriftplan service logs rather than on this page.
      </Alert>

      <Grid container spacing={6}>
        {CRON_GROUPS.map((group) => (
          <Grid item xs={12} key={group.title}>
            <Paper sx={{ p: '24px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                {group.icon}
                <Typography variant="h6">{group.title}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ pb: 4 }}>
                {group.caption}
              </Typography>

              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={4}>
                {group.jobs.map((job) => (
                  <Grid item xs={12} md={6} lg={4} key={job.jobName}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: '16px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 3,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pb: 1 }}
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {job.label}
                          </Typography>
                          <Chip size="small" variant="outlined" label={job.order} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {job.description}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <LoadingButton
                          type="button"
                          color="primary"
                          variant="contained"
                          size="small"
                          endIcon={<PlayArrowIcon />}
                          loading={Boolean(runningJobs[job.jobName])}
                          loadingPosition="end"
                          // Disabled while ANY job starts: these all hit the same
                          // downstream service, and a row of buttons invites a rapid
                          // double-press that would queue two full runs.
                          disabled={anyRunning && !runningJobs[job.jobName]}
                          onClick={() => onTrigger(job)}
                        >
                          Run now
                        </LoadingButton>
                        {renderOutcome(job.jobName)}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
}
